import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, Activity, Heart, Eye, Zap, Shield,
  Database, Cloud, Smartphone, CheckCircle2, Clock,
  MapPin, Building2, Users, TrendingUp, Sparkles, Target, Rocket
} from 'lucide-react';

function HealthcarePage() {
  const navigate = useNavigate();
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observers = sectionsRef.current.map((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      return observer;
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  const services = [
    {
      icon: <Database className="w-8 h-8" />,
      title: "End-to-End HIS",
      description: "Appointments, billing, pharmacy, labs, radiology, inpatient/outpatient management—everything in one seamless system."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Next-Generation EMR",
      description: "Beautiful, intuitive clinical dashboards with e-prescriptions, clinical notes, patient history, lab results, and care pathways."
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "HL7/FHIR Interoperability",
      description: "Smooth data exchange with labs, devices, insurance systems, PACS, and third-party EMRs—powered by global healthcare standards."
    }
  ];

  const kioskApproach = [
    {
      phase: "01",
      title: "Walk In & Log In",
      description: "Walk into the kiosk and log in using your phone number or QR code.",
      icon: <Users className="w-6 h-6" />
    },
    {
      phase: "02",
      title: "Follow Guided Steps",
      description: "Follow the guided steps for each test with simple on-screen instructions.",
      icon: <Smartphone className="w-6 h-6" />
    },
    {
      phase: "03",
      title: "Automatic Processing",
      description: "Smart devices collect and process the readings automatically.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      phase: "04",
      title: "Instant Report Generation",
      description: "A complete digital health report is generated instantly.",
      icon: <CheckCircle2 className="w-6 h-6" />
    },
    {
      phase: "05",
      title: "Mobile Delivery",
      description: "Results are delivered directly to the user's mobile app.",
      icon: <Smartphone className="w-6 h-6" />
    }
  ];

  const strengths = [
    { icon: <Sparkles />, text: "Deep Healthcare Expertise - Years of experience delivering HIS, EMR, HL7 integrations" },
    { icon: <Zap />, text: "Future-Ready Technology - Cloud-first, mobile-ready, IoT-enabled platforms built for scale" },
    { icon: <Smartphone />, text: "Seamless User Experience - Intuitive designs loved by patients, doctors, and healthcare staff" },
    { icon: <Clock />, text: "Fast Delivery & Customization - Solutions tailored to hospitals, clinics, and wellness partners" }
  ];

  const kioskTests = [
    "BMI",
    "Bone Mineral Density (BMD)",
    "Vision / Eye Test",
    "Blood Glucose",
    "ECG",
    "HbA1c"
  ];

  const useCases = [
    { icon: <Building2 />, title: "Pharmacies & Retail Chains", description: "Preventive care at point of sale" },
    { icon: <Users />, title: "Corporate Wellness", description: "Employee health programs" },
    { icon: <Heart />, title: "Community Health Centers", description: "Accessible preventive screening" },
    { icon: <Shield />, title: "Insurance Onboarding", description: "Quick health assessments" },
    { icon: <MapPin />, title: "Public Spaces", description: "Malls, airports, housing communities" },
    { icon: <TrendingUp />, title: "Fitness Centers", description: "Comprehensive health tracking" }
  ];

  return (
    <div className="healthcare-page">
      {/* Hero Section */}
      <section className="hero-section" style={{
        backgroundImage: 'linear-gradient(135deg, rgba(10, 126, 164, 0.7) 0%, rgba(6, 90, 117, 0.7) 100%), url(https://images.unsplash.com/photo-1606206873764-fd15e242df52)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="hero-content">
          <div className="hero-badge">
            <Activity className="w-4 h-4" />
            <span>Healthcare Technology Solutions</span>
          </div>
          <h1 className="hero-title">
            Transforming Healthcare with<br />Smart, Connected Technology
          </h1>
          <p className="hero-subtitle">
            We bring together deep healthcare expertise and cutting-edge technology to build modern, intelligent solutions for hospitals, clinics, and consumers
          </p>
          <div className="hero-cta">
            <Button
              size="lg"
              className="cta-primary"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Started <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="cta-secondary"
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section" ref={(el) => (sectionsRef.current[0] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">About Our Healthcare Solutions</Badge>
            <h2 className="section-title">Empowering the Future of Digital Healthcare</h2>
            <p className="section-description">
              From enterprise-grade HIS/EMR systems to next-generation health kiosks, we deliver products that make healthcare faster, smarter, and more accessible
            </p>
          </div>

          <div className="about-grid">
            <Card className="about-card">
              <CardHeader>
                <Target className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  To build technology that empowers people to take charge of their health. Make quality healthcare accessible, intelligent, and effortless for everyone.
                </p>
              </CardContent>
            </Card>

            <Card className="about-card">
              <CardHeader>
                <Rocket className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Whether it's transforming hospital operations or bringing preventive health checks to everyday locations, we aim to revolutionize healthcare delivery.
                </p>
              </CardContent>
            </Card>

            <Card className="about-card">
              <CardHeader>
                <Heart className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Our Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Years of experience delivering HIS, EMR, HL7 integrations, and connected medical solutions that meet global compliance requirements.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* HIS & EMR Services Section */}
      <section id="services" className="section section-alt" ref={(el) => (sectionsRef.current[1] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">HIS & EMR Solutions</Badge>
            <h2 className="section-title">Powerful HIS & EMR Built for Modern Healthcare</h2>
            <p className="section-description">
              We design and deploy fully digital, easy-to-use healthcare platforms that streamline care—from the front desk to discharge
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <Card key={index} className="service-card">
                <CardHeader>
                  <div className="service-icon">{service.icon}</div>
                  <CardTitle className="service-title">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="service-description">{service.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="platform-features" style={{ marginTop: '3rem', textAlign: 'center' }}>
            <p className="platform-text">
              Our platforms are <strong>secure</strong>, <strong>scalable</strong>, <strong>mobile-friendly</strong>, and built to meet <strong>global compliance requirements</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Smart Health Kiosk Section */}
      <section className="section" ref={(el) => (sectionsRef.current[2] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Smart Health Kiosk</Badge>
            <h2 className="section-title">A 15-Minute Digital Health Check—Anytime, Anywhere</h2>
            <p className="section-description">
              We're redefining preventive care with a self-service Smart Health Kiosk that allows anyone to walk in, complete essential tests, and receive instant results
            </p>
          </div>

          {/* Tests Available */}
          <div style={{ marginBottom: '4rem' }}>
            <h3 className="subsection-title">Tests Available</h3>
            <div className="tests-grid">
              {kioskTests.map((test, index) => (
                <div key={index} className="test-item">
                  <CheckCircle2 className="w-5 h-5 test-check" />
                  <span className="test-name">{test}</span>
                </div>
              ))}
            </div>
            <p className="tests-note">All captured in under 15 minutes, no appointment needed.</p>
          </div>

          {/* How It Works */}
          <div className="approach-timeline">
            {kioskApproach.map((phase, index) => (
              <div key={index} className="approach-item">
                <div className="approach-number">{phase.phase}</div>
                <div className="approach-content">
                  <div className="approach-icon">{phase.icon}</div>
                  <h3 className="approach-title">{phase.title}</h3>
                  <p className="approach-description">{phase.description}</p>
                </div>
                {index < kioskApproach.length - 1 && <div className="approach-line"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where It Fits Section */}
      <section className="section section-alt" ref={(el) => (sectionsRef.current[3] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Where It Fits</Badge>
            <h2 className="section-title">Perfect for Multiple Settings</h2>
          </div>

          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <Card key={index} className="use-case-card">
                <CardHeader>
                  <div className="use-case-icon">{useCase.icon}</div>
                  <CardTitle className="use-case-title">{useCase.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="use-case-description">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Strengths Section */}
      <section className="section" ref={(el) => (sectionsRef.current[4] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Why Choose Us</Badge>
            <h2 className="section-title">Industry-Leading Healthcare Expertise</h2>
          </div>

          <div className="strengths-grid">
            {strengths.map((strength, index) => (
              <div key={index} className="strength-item">
                <div className="strength-icon">{strength.icon}</div>
                <p className="strength-text">{strength.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section" ref={(el) => (sectionsRef.current[5] = el)}>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Healthcare Delivery?</h2>
            <p className="cta-description">
              Let's discuss how our healthcare solutions can benefit your organization
            </p>
            <Button
              size="lg"
              className="cta-primary"
              onClick={() => navigate('/contact')}
            >
              Contact Us Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HealthcarePage;
